import { defineStore } from "pinia";
import * as ChoiceApi from "../ChoiceApi"
import { IntDatabaseIdAndNameModel } from "../Models/IntDatabaseIdAndNameModel";
import * as _ from 'lodash';
import { IUserAndGroupTableItem } from '../Models/User/UserAndGroupItem';
import { ref } from "vue";
import { IMedicationDispenseInfoModel } from '../Api/Prescription';
import { IListItem, IGeographicalStateOrProvinceModel, IUserIdListItemModel } from "../Api/List";

export const useListStore = defineStore('listsStore', () => {

    let allUsersAndGroups: IntDatabaseIdAndNameModel[] | undefined;
    let allUserIds: IUserIdListItemModel[] | undefined;
    const medicationDispenseCode = ref<IMedicationDispenseInfoModel[]>();
    const relationships_ResponsibleParty = ref<IListItem[]>();
    const timeOfDay_ChronicCareManagement = ref<IListItem[]>();
    const chronicCareManagement_NewsLetterDeliveryMethods = ref<IListItem[]>();
    const nyPreschoolBilling_CPSEDistricts = ref<IListItem[]>();
    const paymentReceiptPreference_ReceiptCommunication = ref<IListItem[]>();
    const geographicalStatesAndProvinces = ref<IGeographicalStateOrProvinceModel[]>();

    // Get all the users and groups
    async function getAllUsersAndGroups() : Promise<IntDatabaseIdAndNameModel[]>
    {
        if (!allUsersAndGroups)
        {
            allUsersAndGroups = await ChoiceApi.list.getAllUsersAndGroups();
        }
        return allUsersAndGroups ?? [];
    }

    // Get all the user IDs
    async function getAllUserIds() : Promise<IUserIdListItemModel[] | undefined>
    {
        if (!allUserIds)
        {
            allUserIds = await ChoiceApi.list.getAllUserIds();
        }
        return allUserIds;
    }


    // Get an array with users & groups and indicate which of them are 'favorites'
    async function getUsersGroupsAndCommentFavoritesArray(): Promise<IUserAndGroupTableItem[]>
    {
        // Clone the users and groups
        //const usersAndGroups = {...await getAllUsersAndGroups() as IUserAndGroupTableItem[]};
        const usersAndGroups = _.clone(await getAllUsersAndGroups()) as IUserAndGroupTableItem[];

        // Add a key value for each item
        for(const item of usersAndGroups)
        {
            item.keyValueDataMember = `${item.id}:${item.idType}`;
        }

        // Get favorites
        const favoriteUsersAndGroups = await ChoiceApi.comments.favoriteUsersOrGroups();
        for(const item of usersAndGroups)
        {
            const isFavorite = !!favoriteUsersAndGroups.find(fav => fav.id === item.id && fav.idType === item.idType);
            if (isFavorite)
            {
                item.isFavorite = true;
            }
        }

        // Sort the users & groups
        return _.sortBy(usersAndGroups, ["selected", "isFavorite", "name"]) as IUserAndGroupTableItem[];
    }

    async function getMedicationDispenseInfo(): Promise<IMedicationDispenseInfoModel[] | undefined>
    {
        if (!medicationDispenseCode.value)
        {
            medicationDispenseCode.value = await ChoiceApi.prescription.getMedicationDispenseInfo();
        }
        return medicationDispenseCode.value;
    }

    async function getDefaultDispense(quantityUnitOfMeasureCode_NCLT: string|undefined): Promise<IMedicationDispenseInfoModel | undefined>
    {
        return (await getMedicationDispenseInfo())
                    ?.find(item => true === item.defaultUnitOfMeasureForCode && item.quantityUnitOfMeasureCode_NCLT === quantityUnitOfMeasureCode_NCLT);
    }

    async function getRelationships_ResponsibleParty(): Promise<IListItem[]|undefined> {
        if (!relationships_ResponsibleParty.value)
            relationships_ResponsibleParty.value = (await ChoiceApi.list.getRelationships_ResponsibleParty()) ?? [];
        return relationships_ResponsibleParty.value;
    }

    async function getTimeOfDay_ChronicCareManagement(): Promise<IListItem[]|undefined> {
        if (!timeOfDay_ChronicCareManagement.value)
            timeOfDay_ChronicCareManagement.value = (await ChoiceApi.list.getTimeOfDay_ChronicCareManagement()) ?? [];
        return timeOfDay_ChronicCareManagement.value;
    }

    async function getChronicCareManagement_NewsLetterDeliveryMethods(): Promise<IListItem[]|undefined> {
        if (!chronicCareManagement_NewsLetterDeliveryMethods.value)
            chronicCareManagement_NewsLetterDeliveryMethods.value = (await ChoiceApi.list.chronicCareManagement_NewsLetterDeliveryMethods()) ?? [];
        return chronicCareManagement_NewsLetterDeliveryMethods.value;
    }

    async function getNyPreschoolBilling_CPSEDistricts(): Promise<IListItem[]|undefined> {
        if (!nyPreschoolBilling_CPSEDistricts.value)
            nyPreschoolBilling_CPSEDistricts.value = (await ChoiceApi.list.nyPreschoolBilling_CPSEDistricts()) ?? [];
        return nyPreschoolBilling_CPSEDistricts.value;
    }    
   
    async function getPaymentReceiptPreference_ReceiptCommunication(): Promise<IListItem[]|undefined> {
        if (!paymentReceiptPreference_ReceiptCommunication.value)
            paymentReceiptPreference_ReceiptCommunication.value = (await ChoiceApi.list.paymentReceiptPreference_ReceiptCommunication()) ?? [];
        return paymentReceiptPreference_ReceiptCommunication.value;
    }        
    
    // Get all States and Provinces
    async function getGeographicalStates() : Promise<IGeographicalStateOrProvinceModel[] | undefined>
    {
        if (!geographicalStatesAndProvinces.value)
        {
            geographicalStatesAndProvinces.value = await ChoiceApi.list.getGeographicalStates();
        }
        return geographicalStatesAndProvinces.value;
    }
    
    return { 
        medicationDispenseCode, 
        relationships_ResponsibleParty, 
        timeOfDay_ChronicCareManagement, 
        chronicCareManagement_NewsLetterDeliveryMethods, 
        nyPreschoolBilling_CPSEDistricts,
        paymentReceiptPreference_ReceiptCommunication,
        geographicalStatesAndProvinces,
        getAllUsersAndGroups, 
        getAllUserIds, 
        getUsersGroupsAndCommentFavoritesArray, 
        getMedicationDispenseInfo, 
        getDefaultDispense, 
        getRelationships_ResponsibleParty, 
        getTimeOfDay_ChronicCareManagement,
        getChronicCareManagement_NewsLetterDeliveryMethods, 
        getNyPreschoolBilling_CPSEDistricts,
        getPaymentReceiptPreference_ReceiptCommunication,
        getGeographicalStates
    };
});